import { useState, useEffect, lazy, Suspense } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Fish, Settings, Heart, User, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { useUser } from '../contexts/UserContext';
import { NotificationsBell } from './NotificationsBell';

// Lazy load AuthModal to avoid loading framer-motion on initial page load
const AuthModal = lazy(() => import('./AuthModal').then(m => ({ default: m.AuthModal })));

const navItems = [
  { path: '/', label: 'Accueil' },
  { path: '/histoire', label: 'Histoire & Culture' },
  { path: '/varietes', label: 'Variétés' },
  { path: '/eleveurs', label: 'Éleveurs' },
  { path: '/soins', label: 'Soins' },
  { path: '/alimentation', label: 'Alimentation' },
  { path: '/outils-bassin', label: 'Outils Bassin' },
  { path: '/lexique', label: 'Lexique' },
  { path: '/quiz', label: 'Quiz' },
  { path: '/concours', label: 'Concours' },
  { path: '/blog', label: 'Blog' },
  { path: '/galerie', label: 'Galerie' },
  { path: '/faq', label: 'FAQ' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const { isAuthenticated: isAdmin } = useAuth();
  const { user, isAuthenticated: isUserLoggedIn } = useUser();
  const { favoritesCount } = useFavorites();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navBgClass = isScrolled || !isHome
    ? 'navbar-glass shadow-sm'
    : 'bg-transparent';

  const textColorClass = isScrolled || !isHome
    ? 'text-[#0F172A]'
    : 'text-white';

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-logo"
            className={`flex items-center gap-3 ${textColorClass} hover:opacity-80 transition-opacity`}
          >
            <Fish className="w-8 h-8" />
            <span className="font-['Playfair_Display'] text-xl font-semibold">
              Nishikigoi.fr
            </span>
          </Link>

          {/* Desktop Navigation - Only visible on extra large screens */}
          <nav className="hidden xl:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${textColorClass} hover:bg-black/10 ${
                  location.pathname === item.path ? 'bg-black/10' : ''
                }`}
              >
                {item.label}
              </Link>
            ))}
            {/* Favorites Button */}
            <Link
              to="/favoris"
              data-testid="nav-favoris"
              className={`relative ml-2 p-2 rounded-full transition-colors ${textColorClass} hover:bg-black/10`}
              title="Mes Favoris"
            >
              <Heart className={`w-5 h-5 ${favoritesCount > 0 ? 'fill-[#D32F2F] text-[#D32F2F]' : ''}`} />
              {favoritesCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#D32F2F] text-white text-[10px] rounded-full flex items-center justify-center font-medium">
                  {favoritesCount > 9 ? '9+' : favoritesCount}
                </span>
              )}
            </Link>

            {/* Notifications Bell */}
            <NotificationsBell />

            {/* User Account Button */}
            {isUserLoggedIn ? (
              <Link 
                to="/compte" 
                className="relative ml-2 flex items-center gap-2 hover:opacity-80 transition-opacity"
                data-testid="nav-account-link"
              >
                {user?.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name}
                    className="w-8 h-8 rounded-full border-2 border-white/50"
                  />
                ) : (
                  <div className={`w-8 h-8 rounded-full bg-[#D32F2F] flex items-center justify-center text-white text-sm font-medium`}>
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                )}
                <span className={`text-sm font-medium ${textColorClass} hidden xl:inline`}>
                  Mon Compte
                </span>
              </Link>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                data-testid="nav-login-btn"
                className={`ml-2 px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${textColorClass} hover:bg-black/10 border border-current/20`}
              >
                <User className="w-4 h-4 inline mr-1" />
                Connexion
              </button>
            )}
          </nav>

          {/* Mobile Menu Button - Visible on screens smaller than xl */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={isMobileMenuOpen}
            className={`xl:hidden p-2 rounded-lg ${textColorClass} hover:bg-black/10 transition-colors`}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className="xl:hidden fixed inset-x-0 top-20 bottom-0 navbar-glass border-t border-stone-200 overflow-y-auto shadow-lg z-40"
        >
          <nav className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1 pb-24">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`mobile-nav-${item.path.replace('/', '') || 'home'}`}
              className={`px-4 py-3 text-[#0F172A] font-medium rounded-lg hover:bg-stone-100 transition-colors ${
                location.pathname === item.path ? 'bg-stone-100' : ''
              }`}
            >
              {item.label}
            </Link>
          ))}
          <Link to="/favoris" className="mt-2">
            <Button
              data-testid="mobile-nav-favoris-btn"
              variant="outline"
              className="w-full rounded-full flex items-center justify-center gap-2 border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white"
            >
              <Heart className={`w-4 h-4 ${favoritesCount > 0 ? 'fill-[#D32F2F] text-[#D32F2F]' : ''}`} />
              Mes Favoris
              {favoritesCount > 0 && (
                <span className="w-5 h-5 bg-[#D32F2F] text-white text-xs rounded-full flex items-center justify-center">
                  {favoritesCount}
                </span>
              )}
            </Button>
          </Link>
          
          {/* User Account Button (Mobile) */}
          {isUserLoggedIn ? (
            <Link to="/compte" className="mt-2 block">
              <div className="flex items-center gap-3 px-4 py-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors">
                {user?.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name}
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#D32F2F] flex items-center justify-center text-white font-medium">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                )}
                <div className="flex-1">
                  <p className="font-medium text-[#0F172A]">{user?.name}</p>
                  <p className="text-sm text-[#0F172A]/60">Mon Compte</p>
                </div>
                <ChevronRight className="w-5 h-5 text-[#0F172A]/40" />
              </div>
            </Link>
          ) : (
            <Button
              onClick={() => {
                setIsMobileMenuOpen(false);
                setShowAuthModal(true);
              }}
              data-testid="mobile-login-user-btn"
              className="mt-2 w-full rounded-full bg-[#D32F2F] hover:bg-[#B71C1C]"
            >
              <User className="w-4 h-4 mr-2" />
              Connexion / Inscription
            </Button>
          )}
          
          {!isAdmin && (
            <Link to="/login" className="mt-2">
              <Button
                data-testid="mobile-nav-login-btn"
                variant="outline"
                className="w-full rounded-full border-[#D32F2F] text-[#D32F2F]"
              >
                <Settings className="w-4 h-4 mr-2" />
                Administration
              </Button>
            </Link>
          )}
          {isAdmin && (
            <Link to="/admin" className="mt-2">
              <Button
                data-testid="mobile-nav-admin-btn"
                className="w-full rounded-full bg-[#0F172A] hover:bg-[#1e293b]"
              >
                <Settings className="w-4 h-4 mr-2" />
                Tableau de bord
              </Button>
            </Link>
          )}
        </nav>
        </div>
      )}
      
      {/* Auth Modal - Lazy loaded */}
      {showAuthModal && (
        <Suspense fallback={null}>
          <AuthModal 
            isOpen={showAuthModal} 
            onClose={() => setShowAuthModal(false)} 
          />
        </Suspense>
      )}
    </header>
  );
};

export default Navbar;
